import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard,
  FileText,
  ClipboardList,
  Bell,
  Users,
  Images,
  Package,
  Cog,
  Wrench,
  Plus,
  LogOut,
  Factory,
  DollarSign,
  Landmark,
  ReceiptText,
  Settings as SettingsIcon,
} from "lucide-react";
const nav = [
  { to: "/admin", label: "Dashboard", short: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard-link" },
  { to: "/solicitacoes", label: "Orçamentos", short: "Orçam.", icon: ClipboardList, testId: "nav-requests-link" },
  { to: "/clientes-admin", label: "Clientes", short: "Clientes", icon: Users, testId: "nav-customers-link" },
  { to: "/precos-admin", label: "Materiais/Preços", short: "Mat./Preços", icon: DollarSign, testId: "nav-cutting-prices-link" },
  { to: "/pedidos-admin", label: "Pedidos / Produção", short: "Pedidos", icon: Package, testId: "nav-orders-link" },
  { to: "/producao", label: "Ordens de Produção", short: "OPs", icon: Factory, testId: "nav-production-link" },
  { to: "/financeiro", label: "Financeiro", short: "Financ.", icon: Landmark, testId: "nav-financeiro-link" },
  { to: "/financeiro/pagar", label: "Contas a Pagar", short: "Pagar", icon: ReceiptText, testId: "nav-payables-link" },
  { to: "/galeria-admin", label: "Galeria", short: "Galeria", icon: Images, testId: "nav-gallery-admin-link" },
  { to: "/notificacoes", label: "Notificações", short: "Notif.", icon: Bell, testId: "nav-notifications-link" },
  { to: "/servicos-admin", label: "Serviços", short: "Serviços", icon: Wrench, testId: "nav-services-link" },
  { to: "/maquinas-admin", label: "Máquinas", short: "Máquinas", icon: Cog, testId: "nav-machines-link" },
  { to: "/usuarios", label: "Usuários", short: "Usuários", icon: Users, testId: "nav-users-link" },
  { to: "/configuracoes", label: "Configurações", short: "Config.", icon: SettingsIcon, testId: "nav-settings-link" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav_ = useNavigate();

  return (
    <div className="min-h-screen flex bg-[hsl(var(--background))] text-slate-100">
      <aside className="hidden md:flex flex-col w-64 border-r border-slate-800 bg-slate-950/60 backdrop-blur">
        <div className="px-5 py-6 border-b border-slate-800">
          <Logo />
          <div className="mt-2 text-[10px] mono uppercase tracking-[0.25em] text-orange-400/80">
            área do cliente
          </div>
        </div>

        <button
          data-testid="nav-new-quote-link"
          onClick={() => nav_("/orcamentos/novo")}
          className="mx-4 mt-5 mb-2 flex items-center justify-center gap-2 pill-accent hover:opacity-95 rounded-md px-3 py-2.5 text-sm font-semibold tracking-wide transition"
        >
          <Plus className="w-4 h-4" />
          Novo Orçamento
        </button>

        <nav className="mt-3 px-3 flex-1 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/dashboard"}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition ${
                  isActive
                    ? "bg-orange-500/10 text-orange-300 border-l-2 border-orange-400"
                    : "text-slate-300 hover:bg-slate-800/60 hover:text-white border-l-2 border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="text-xs text-slate-400 truncate" data-testid="current-user-email">
            {user?.email}
          </div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-slate-800/60"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-950/80 backdrop-blur">
        <Logo compact />
        <button
          onClick={logout}
          className="text-xs text-slate-400"
          data-testid="logout-button-mobile"
        >
          Sair
        </button>
      </div>

      <main className="flex-1 min-w-0 md:pl-0 pt-16 md:pt-0">
        {/* Mobile bottom nav */}
        <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-slate-950/95 border-t border-slate-800 grid grid-cols-5 text-[10px]">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/dashboard"}
              className={({ isActive }) =>
                `flex flex-col items-center py-2 gap-1 ${
                  isActive ? "text-orange-400" : "text-slate-400"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.short || n.label}
            </NavLink>
          ))}
          <NavLink
            to="/orcamentos/novo"
            className="flex flex-col items-center py-2 gap-1 text-orange-400"
          >
            <Plus className="w-4 h-4" />
            Novo
          </NavLink>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-8">
          {children}
        </div>
      </main>
    </div>
  );
}
